/**
 * Shared UI primitives for the Service (Repairman) module.
 * Keeps visuals consistent across Dashboard, WorkSheet list/detail,
 * Calendar, Customers, Vehicles, Parts and Photos screens.
 */
import React from 'react';
import {ActivityIndicator, Platform, StyleSheet, Text, TouchableOpacity, View,} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTopInset} from '../helpers/safeArea';

/* ─── Status palette (canonical) ─── */
export const SERVICE_STATUS = {
    pending:     { key: 'pending',     color: '#F59E0B', icon: 'clock-outline'      },
    scheduled:   { key: 'scheduled',   color: '#2563EB', icon: 'calendar-clock'     },
    in_progress: { key: 'in_progress', color: '#2563EB', icon: 'wrench'             },
    waiting:     { key: 'waiting',     color: '#64748B', icon: 'timer-sand'         },
    completed:   { key: 'completed',   color: '#16A34A', icon: 'check-circle'       },
    invoiced:    { key: 'invoiced',    color: '#7C3AED', icon: 'receipt'            },
    cancelled:   { key: 'cancelled',   color: '#DC2626', icon: 'close-circle'       },
};

export const statusColor = (status) =>
    SERVICE_STATUS[status]?.color || SERVICE_STATUS.pending.color;

export const statusIcon = (status) =>
    SERVICE_STATUS[status]?.icon || SERVICE_STATUS.pending.icon;

/* ─── Status Badge ─── */
export const StatusBadge = ({ status, t, size = 'sm', style }) => {
    const cfg = SERVICE_STATUS[status] || SERVICE_STATUS.pending;
    const label = t ? t(`repair_status_${cfg.key}`) : cfg.key;
    const isLg = size === 'lg';
    return (
        <View style={[
            styles.badge,
            { backgroundColor: cfg.color + (isLg ? 'ff' : '22') },
            isLg && { paddingHorizontal: 12, paddingVertical: 6 },
            style,
        ]}>
            <Icon
                name={cfg.icon}
                size={isLg ? 14 : 12}
                color={isLg ? '#fff' : cfg.color}
                style={{ marginRight: 4 }}
            />
            <Text style={[
                styles.badgeText,
                { color: isLg ? '#fff' : cfg.color },
                isLg && { fontSize: 12 },
            ]}>
                {label}
            </Text>
        </View>
    );
};

/* ─── Empty state ─── */
export const ServiceEmptyState = ({
    icon = 'inbox-outline', title, subtitle, actionLabel, onAction, theme,
}) => (
    <View style={styles.emptyWrap}>
        <View style={[styles.emptyIconWrap, { backgroundColor: theme.colors.primary + '12' }]}>
            <Icon name={icon} size={44} color={theme.colors.primary} />
        </View>
        {!!title && <Text style={[styles.emptyTitle, { color: theme.colors.text }]}>{title}</Text>}
        {!!subtitle && (
            <Text style={[styles.emptySubtitle, { color: theme.colors.subText }]}>{subtitle}</Text>
        )}
        {!!onAction && !!actionLabel && (
            <TouchableOpacity
                style={[styles.emptyBtn, { backgroundColor: theme.colors.primary }]}
                onPress={onAction}
                activeOpacity={0.8}
            >
                <Text style={styles.emptyBtnText}>{actionLabel}</Text>
            </TouchableOpacity>
        )}
    </View>
);

/* ─── Loading state ─── */
export const ServiceLoading = ({ theme, label }) => (
    <View style={styles.loadingWrap}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
        {!!label && (
            <Text style={[styles.loadingLabel, { color: theme.colors.subText }]}>{label}</Text>
        )}
    </View>
);

/* ─── Error state ─── */
export const ServiceErrorState = ({ theme, message, onRetry, retryLabel }) => (
    <View style={styles.emptyWrap}>
        <View style={[styles.emptyIconWrap, { backgroundColor: (theme.colors.error || '#EF5350') + '18' }]}>
            <Icon name="alert-circle-outline" size={44} color={theme.colors.error || '#EF5350'} />
        </View>
        <Text style={[styles.emptyTitle, { color: theme.colors.text }]}>{message}</Text>
        {!!onRetry && (
            <TouchableOpacity
                style={[styles.emptyBtn, { backgroundColor: theme.colors.error || '#EF5350' }]}
                onPress={onRetry}
                activeOpacity={0.8}
            >
                <Text style={styles.emptyBtnText}>{retryLabel || 'Retry'}</Text>
            </TouchableOpacity>
        )}
    </View>
);

/* ─── Screen header ─── */
export const ServiceHeader = ({
    theme, title, subtitle, onBack, rightIcon, onRightPress, rightColor,
}) => {
    const topInset = useTopInset(); // adapts header to notch / Dynamic Island per device

    return (
    <View style={[styles.header, { borderBottomColor: theme.colors.border, paddingTop: topInset + 10 }]}>
        {onBack ? (
            <TouchableOpacity
                onPress={onBack}
                style={[styles.iconBtn, {
                    backgroundColor: theme.colors.card,
                    borderWidth: 1,
                    borderColor: theme.colors.border,
                }]}
            >
                <Icon name="chevron-left" size={26} color={theme.colors.text} />
            </TouchableOpacity>
        ) : <View style={{ width: 40 }} />}
        <View style={{ flex: 1, marginHorizontal: 10 }}>
            <Text style={[styles.headerTitle, { color: theme.colors.text }]} numberOfLines={1}>
                {title}
            </Text>
            {!!subtitle && (
                <Text style={[styles.headerSubtitle, { color: theme.colors.subText }]} numberOfLines={1}>
                    {subtitle}
                </Text>
            )}
        </View>
        {rightIcon ? (
            <TouchableOpacity
                onPress={onRightPress}
                style={[styles.iconBtn, { backgroundColor: rightColor || theme.colors.primary }]}
            >
                <Icon name={rightIcon} size={20} color="#fff" />
            </TouchableOpacity>
        ) : <View style={{ width: 40 }} />}
    </View>
    );
};

/* ─── Sync indicator (tiny, top-right) ─── */
export const SyncIndicator = ({ syncing, theme }) => {
    if (!syncing) return null;
    return (
        <View style={[styles.syncRow, { backgroundColor: theme.colors.primary + '18' }]}>
            <ActivityIndicator size="small" color={theme.colors.primary} />
            <Text style={[styles.syncText, { color: theme.colors.primary }]}>Syncing…</Text>
        </View>
    );
};

const styles = StyleSheet.create({
    badge: {
        flexDirection: 'row', alignItems: 'center',
        paddingHorizontal: 8, paddingVertical: 3, borderRadius: 999,
        alignSelf: 'flex-start',
    },
    badgeText: { fontSize: 11, fontWeight: '700', textTransform: 'uppercase', letterSpacing: 0.3 },

    emptyWrap: {
        alignItems: 'center', justifyContent: 'center',
        paddingVertical: 60, paddingHorizontal: 32,
    },
    emptyIconWrap: {
        width: 88, height: 88, borderRadius: 44,
        alignItems: 'center', justifyContent: 'center', marginBottom: 16,
    },
    emptyTitle: { fontSize: 16, fontWeight: '700', textAlign: 'center', marginBottom: 6 },
    emptySubtitle: { fontSize: 13, textAlign: 'center', lineHeight: 19 },
    emptyBtn: {
        marginTop: 20, paddingHorizontal: 24, paddingVertical: 12, borderRadius: 999,
    },
    emptyBtnText: { color: '#fff', fontWeight: '700', fontSize: 14 },

    loadingWrap: {
        alignItems: 'center', justifyContent: 'center', paddingVertical: 40, gap: 10,
    },
    loadingLabel: { fontSize: 13, fontWeight: '500' },

    header: {
        flexDirection: 'row', alignItems: 'center',
        paddingHorizontal: 16,
        // paddingTop applied dynamically via useTopInset() in ServiceHeader
        paddingBottom: 12,
        borderBottomWidth: StyleSheet.hairlineWidth,
    },
    iconBtn: {
        width: 40, height: 40, borderRadius: 20,
        alignItems: 'center', justifyContent: 'center',
    },
    headerTitle: { fontSize: 22, fontWeight: '800', letterSpacing: -0.4 },
    headerSubtitle: { fontSize: 12, marginTop: 2 },

    syncRow: {
        flexDirection: 'row', alignItems: 'center', gap: 6,
        alignSelf: 'center', paddingHorizontal: 10, paddingVertical: 4,
        borderRadius: 999, marginVertical: 4,
    },
    syncText: { fontSize: 11, fontWeight: '600' },
});

export default {
    StatusBadge,
    ServiceEmptyState,
    ServiceLoading,
    ServiceErrorState,
    ServiceHeader,
    SyncIndicator,
    SERVICE_STATUS,
    statusColor,
    statusIcon,
};

